import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseConfig';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  serverTimestamp // Import serverTimestamp
} from 'firebase/firestore';
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from 'firebase/storage';
import ManagePhotosModal from './ManagePhotosModal';
import './AdminImageManagement.css'; // Import the new CSS file

const AdminImageManagement = ({ user, setError }) => {
  const [sedes, setSedes] = useState([]); // Top-level sedes
  const [eventSpaces, setEventSpaces] = useState([]); // Actual event spaces (salons)
  const [currentImage, setCurrentImage] = useState({ title: '', url: '', file: null, type: 'sede' }); // Added type
  const [editingImageId, setEditingImageId] = useState(null);
  const [selectedSedeId, setSelectedSedeId] = useState(''); // For linking event spaces to sedes
  const [managingPhotosOf, setManagingPhotosOf] = useState(null);

  const eventSpacesCollectionRef = collection(db, 'eventSpaces');

  useEffect(() => {
    if (user) {
      const fetchData = async () => {
        try {
          const querySnapshot = await getDocs(eventSpacesCollectionRef);
          const allEventSpaces = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

          const fetchedSedes = [];
          const fetchedEventSpaces = [];

          allEventSpaces.forEach(space => {
            if (space.type === 'sede') {
              fetchedSedes.push(space);
            } else if (space.type === 'eventSpace') {
              fetchedEventSpaces.push(space);
            } else {
              console.warn(`Document ${space.id} in eventSpaces collection is missing a 'type' field or has an invalid type. Please update it in Firestore.`);
            }
          });

          setSedes(fetchedSedes);
          setEventSpaces(fetchedEventSpaces);
        } catch (err) {
          setError('Error al cargar los datos de espacios de eventos. Por favor, revise las reglas de seguridad de Firestore.');
          console.error(err);
        }
      };
      fetchData();
    }
  }, [user, setError]);

  const handleImageSubmit = async (e) => {
    e.preventDefault();
    if (editingImageId) {
      await updateImage();
    } else {
      await addImage();
    }
  };

  const addImage = async () => {
    try {
      let imageUrlToSave = currentImage.url;

      if (currentImage.file) {
        const imageRef = ref(storage, `eventSpaces/${currentImage.file.name}`);
        await uploadBytes(imageRef, currentImage.file);
        imageUrlToSave = await getDownloadURL(imageRef);
      } else if (!currentImage.url) {
        console.warn('No image file or URL provided for new image.');
      }

      const docData = {
        title: currentImage.title,
        imageUrl: imageUrlToSave,
        type: currentImage.type,
        createdAt: serverTimestamp(), // Add createdAt for new documents
      };

      if (currentImage.type === 'eventSpace') {
        docData.sedeId = selectedSedeId || null;
      }

      const docRef = await addDoc(eventSpacesCollectionRef, docData);

      if (currentImage.type === 'sede') {
        setSedes([...sedes, { id: docRef.id, ...docData }]);
      } else {
        setEventSpaces([...eventSpaces, { id: docRef.id, ...docData }]);
      }

      setCurrentImage({ title: '', url: '', file: null, type: 'sede' });
      setSelectedSedeId('');
    } catch (error) {
      setError(`Error al agregar el espacio de evento a Firestore: ${error.message}`);
      console.error('Error adding event space: ', error);
    }
  };

  const updateImage = async () => {
    try {
      let imageUrlToSave = currentImage.url;

      if (currentImage.file) {
        const imageRef = ref(storage, `eventSpaces/${currentImage.file.name}`);
        await uploadBytes(imageRef, currentImage.file);
        imageUrlToSave = await getDownloadURL(imageRef);
      } else if (!currentImage.url && !eventSpaces.find(img => img.id === editingImageId)?.imageUrl && !sedes.find(img => img.id === editingImageId)?.imageUrl) {
        console.warn('No new image file or URL provided for update, and no existing URL.');
      }

      const docData = {
        title: currentImage.title,
        imageUrl: imageUrlToSave,
        type: currentImage.type,
      };

      if (currentImage.type === 'eventSpace') {
        docData.sedeId = selectedSedeId || null;
      }

      const imageDoc = doc(db, 'eventSpaces', editingImageId);
      await updateDoc(imageDoc, docData);

      if (currentImage.type === 'sede') {
        setSedes(sedes.map(s => s.id === editingImageId ? { id: editingImageId, ...docData } : s));
        setEventSpaces(eventSpaces.filter(es => es.id !== editingImageId));
      } else {
        setEventSpaces(eventSpaces.map(es => es.id === editingImageId ? { id: editingImageId, ...docData } : es));
        setSedes(sedes.filter(s => s.id !== editingImageId));
      }

      setEditingImageId(null);
      setCurrentImage({ title: '', url: '', file: null, type: 'sede' });
      setSelectedSedeId('');
    } catch (error) {
      setError(`Error al actualizar el espacio de evento en Firestore: ${error.message}`);
      console.error('Error updating event space: ', error);
    }
  };

  const deleteImage = async (id, url, type) => {
    try {
      await deleteDoc(doc(db, 'eventSpaces', id));

      const imageRef = ref(storage, url);
      await deleteObject(imageRef);

      if (type === 'sede') {
        setSedes(sedes.filter(s => s.id !== id));
      } else {
        setEventSpaces(eventSpaces.filter(es => es.id !== id));
      }
    } catch (error) {
      setError('Error al eliminar el espacio de evento.');
      console.error('Error deleting event space: ', error);
    }
  };

  const startEditing = (img) => {
    setEditingImageId(img.id);
    setCurrentImage({ title: img.title, url: img.imageUrl, file: null, type: img.type || 'sede' });
    setSelectedSedeId(img.sedeId || '');
  };

  return (
    <section className="admin-image-management-section">
      <h2>Gestión de Sedes y Espacios de Eventos</h2>
      <form onSubmit={handleImageSubmit} className="admin-image-management-form">
        <input
          type="text"
          placeholder="Título"
          value={currentImage.title}
          onChange={(e) => setCurrentImage({ ...currentImage, title: e.target.value })}
          required
        />
        <input
          type="text"
          placeholder="URL de Imagen (opcional)"
          value={currentImage.url}
          onChange={(e) => setCurrentImage({ ...currentImage, url: e.target.value })}
        />
        <input
          type="file"
          onChange={(e) => setCurrentImage({ ...currentImage, file: e.target.files[0] })}
        />
        <select
          value={currentImage.type}
          onChange={(e) => {
            setCurrentImage({ ...currentImage, type: e.target.value });
            setSelectedSedeId(''); // Reset sede selection when type changes
          }}
        >
          <option value="sede">Sede Principal</option>
          <option value="eventSpace">Espacio de Evento (Salón)</option>
        </select>

        {currentImage.type === 'eventSpace' && (
          <select
            value={selectedSedeId}
            onChange={(e) => setSelectedSedeId(e.target.value)}
            required={currentImage.type === 'eventSpace'}
          >
            <option value="">-- Seleccionar Sede Padre --</option>
            {sedes.map(sede => (
              <option key={sede.id} value={sede.id}>{sede.title}</option>
            ))}
          </select>
        )}

        <button type="submit">{editingImageId ? 'Actualizar' : 'Agregar'} Espacio</button>
        {editingImageId && (
          <button onClick={() => { setEditingImageId(null); setCurrentImage({ title: '', url: '', file: null, type: 'sede' }); setSelectedSedeId(''); }} className="cancel-edit-button">Cancelar Edición</button>
        )}
      </form>

      <h3>Sedes Principales</h3>
      <div className="admin-list-container">
        {sedes.map(sede => (
          <div key={sede.id} className="admin-list-item">
            <img src={sede.imageUrl} alt={sede.title} className="admin-list-item-image" />
            <div className="admin-list-item-content">
              <h4 className="admin-list-item-title">{sede.title} (Sede)</h4>
              <div className="admin-list-item-actions">
                <button onClick={() => startEditing(sede)}>Editar</button>
                <button onClick={() => deleteImage(sede.id, sede.imageUrl, sede.type)}>Eliminar</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <h3>Espacios de Eventos (Salones)</h3>
      <div className="admin-list-container">
        {eventSpaces.map(space => (
          <div key={space.id} className="admin-list-item">
            <img src={space.imageUrl} alt={space.title} className="admin-list-item-image" />
            <div className="admin-list-item-content">
              <h4 className="admin-list-item-title">{space.title} (Salón - Sede: {sedes.find(s => s.id === space.sedeId)?.title || 'N/A'})</h4>
              <div className="admin-list-item-actions">
                <button onClick={() => startEditing(space)}>Editar</button>
                <button onClick={() => deleteImage(space.id, space.imageUrl, space.type)}>Eliminar</button>
                <button onClick={() => setManagingPhotosOf(space.id)}>Gestionar Fotos</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {managingPhotosOf && (
        <ManagePhotosModal
          eventSpaceId={managingPhotosOf}
          onClose={() => setManagingPhotosOf(null)}
        />
      )}
    </section>
  );
};

export default AdminImageManagement;
